import turfDistance from '@turf/distance';
import { Feature, Geometry } from 'geojson';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Layer, Source } from 'react-map-gl/mapbox';
import { milesToMeters } from '@alltrails/data-formatting/distanceConverters';
import { getUnitAbbreviation } from '@alltrails/data-formatting/utils';
import { COLOR_TEXT_PRIMARY, SPACE_100, SPACE_50 } from '@alltrails/denali/tokens';
import useMap from '../../hooks/useMap';
import { useAppSelector } from '../../redux';
import { LngLat } from '../../types/Geo';
import { defaultSymbolLayerLayoutProps, distanceMarkersId, getLayerBeforeId } from '../../utils/layers';
import useSvgImages, { SvgDefinition } from '../../utils/useSvgImages';
import distanceMarkerSvg, { svgShadowSize } from './distanceMarkerSvg';

const svgDefinitions: SvgDefinition[] = [distanceMarkerSvg];

const metersInAMile = milesToMeters(1);

const getPolylineMeters = (polyline: LngLat[]) =>
  polyline.reduce((meters, coord, index) => {
    if (index === 0) {
      return 0;
    }
    return meters + turfDistance(polyline[index - 1], coord, { units: 'meters' });
  }, 0);

type DistanceMarkersProps = {
  // Each inner array is one continuous polyline. Markers count up from 1 within each
  // polyline and reset between them, so disjoint polylines (e.g. multi-segment custom
  // routes) don't render markers across the gaps.
  polylines: LngLat[][];
};

const DistanceMarkers = ({ polylines }: DistanceMarkersProps) => {
  const map = useMap();
  const intl = useIntl();
  const displayMetric = useAppSelector(state => state.context.displayMetric);
  const sourceId = 'distance-markers';

  const { imagesAreLoaded } = useSvgImages(svgDefinitions);

  const features = useMemo(() => {
    const validPolylines = polylines.filter(polyline => polyline.length >= 2);
    if (validPolylines.length === 0) {
      return [];
    }

    const totalMeters = validPolylines.reduce((sum, polyline) => sum + getPolylineMeters(polyline), 0);

    let useTenths = false;
    if (displayMetric) {
      useTenths = totalMeters <= 1000;
    } else {
      useTenths = totalMeters <= metersInAMile;
    }

    let metersBetweenMarkers = 0;
    if (displayMetric) {
      metersBetweenMarkers = useTenths ? 100 : 1000;
    } else {
      metersBetweenMarkers = useTenths ? metersInAMile / 10 : metersInAMile;
    }

    const unit = getUnitAbbreviation(intl, displayMetric ? 'kilometer' : 'mile').toLowerCase();
    const allMarkers: Feature<Geometry, { text: string }>[] = [];

    validPolylines.forEach(polyline => {
      let distanceSinceLastMarker = 0;
      let markerNumber = 1;

      polyline.forEach((coordinate, index) => {
        if (index === 0) {
          return;
        }

        const accumulatedDistance = distanceSinceLastMarker + turfDistance(polyline[index - 1], coordinate, { units: 'meters' });
        if (accumulatedDistance >= metersBetweenMarkers) {
          const value = useTenths ? markerNumber / 10 : markerNumber;
          allMarkers.push({
            geometry: { type: 'Point', coordinates: coordinate },
            properties: { text: `${value} ${unit}` },
            type: 'Feature'
          });

          distanceSinceLastMarker = 0;
          markerNumber += 1;
        } else {
          distanceSinceLastMarker = accumulatedDistance;
        }
      });
    });

    return allMarkers;
  }, [polylines, displayMetric, intl]);

  if (!imagesAreLoaded) {
    return null;
  }

  return (
    <Source id={sourceId} type="geojson" data={{ type: 'FeatureCollection', features }}>
      <Layer
        id={distanceMarkersId}
        source={sourceId}
        beforeId={getLayerBeforeId(map, distanceMarkersId)}
        type="symbol"
        layout={{
          ...defaultSymbolLayerLayoutProps,
          'icon-image': distanceMarkerSvg.name,
          'icon-text-fit': 'both',
          'icon-text-fit-padding': [svgShadowSize + SPACE_50, svgShadowSize + SPACE_100, svgShadowSize + SPACE_50, svgShadowSize + SPACE_100],
          'text-anchor': 'center',
          'text-field': ['get', 'text']
        }}
        paint={{
          'text-color': COLOR_TEXT_PRIMARY
        }}
      />
    </Source>
  );
};

export default DistanceMarkers;
